// Google Analytics 4 Event Tracking Utility
const GA_ID = 'G-98L9ZE2DVB';
const API = process.env.REACT_APP_BACKEND_URL;

// ---- First-party analytics ("our own pixel") ------------------------------
// Persistent, anonymous visitor id + rolling 30-min session id. All page views
// and instrumented events are mirrored to our own backend (/api/analytics/collect)
// so the admin "Web Analytics" tab can analyse everything without depending on
// Google/Facebook dashboards.
const VISITOR_KEY = 'ok_visitor_id';
const SESSION_KEY = 'ok_session';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 min inactivity

function _uuid() {
  try { return crypto.randomUUID(); }
  catch (e) { return 'v-' + Date.now() + '-' + Math.random().toString(36).slice(2); }
}

function _visitorId() {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) { v = _uuid(); localStorage.setItem(VISITOR_KEY, v); }
    return v;
  } catch (e) { return 'anon'; }
}

function _sessionId() {
  try {
    const now = Date.now();
    const raw = localStorage.getItem(SESSION_KEY);
    let s = raw ? JSON.parse(raw) : null;
    if (!s || (now - s.last) > SESSION_TIMEOUT) {
      s = { id: _uuid(), last: now };
    } else {
      s.last = now;
    }
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    return s.id;
  } catch (e) { return 'sess'; }
}

function _currentUserId() {
  try {
    const raw = localStorage.getItem('user');
    if (raw) { const u = JSON.parse(raw); return u?.id || u?.user_id || null; }
    return localStorage.getItem('user_id') || null;
  } catch (e) { return null; }
}

// Fire-and-forget send to our own collector. Uses sendBeacon when possible so
// events survive page unloads (important for exit/conversion tracking).
function _send(type, name, extra = {}) {
  if (!API) return;
  try {
    const payload = {
      events: [{
        visitor_id: _visitorId(),
        session_id: _sessionId(),
        type,
        name,
        path: window.location.pathname + window.location.search,
        referrer: document.referrer || '',
        utm: getUTM(),
        user_id: _currentUserId(),
        screen_w: window.screen?.width || null,
        screen_h: window.screen?.height || null,
        lang: navigator.language || '',
        ...extra,
      }],
    };
    const url = `${API}/api/analytics/collect`;
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
    }
  } catch (e) { /* never break the app for analytics */ }
}

function gtag() {
  if (window.gtag) {
    window.gtag(...arguments);
  }
}

// Meta (Facebook) Pixel — base pixel is initialised in public/index.html.
function fbq() {
  if (window.fbq) {
    try { window.fbq(...arguments); } catch (e) { /* no-op */ }
  }
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

// Persist UTM params from the current URL (first-touch) so they can be
// attached to conversion events for ad attribution.
export function captureUTM() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    UTM_KEYS.forEach((k) => { const v = params.get(k); if (v) found[k] = v; });
    if (Object.keys(found).length) {
      localStorage.setItem('utm_params', JSON.stringify({ ...found, _ts: Date.now() }));
    }
  } catch (e) { /* no-op */ }
}

export function getUTM() {
  try {
    const raw = localStorage.getItem('utm_params');
    if (!raw) return {};
    const obj = JSON.parse(raw);
    const out = {};
    UTM_KEYS.forEach((k) => { if (obj[k]) out[k] = obj[k]; });
    return out;
  } catch (e) { return {}; }
}

// Track custom events
export function trackEvent(eventName, params = {}) {
  gtag('event', eventName, params);
  // Mirror to our first-party pixel. `value`/`currency` are promoted so
  // purchases carry revenue for conversion analytics.
  _send('event', eventName, {
    value: params?.value || null,
    currency: params?.currency || null,
    props: params,
  });
}

// Map a report landing path → the funnel report_type key (used by the admin
// checkout funnel). Kept in one place so form-open events are consistently keyed.
const REPORT_PATHS = {
  "/mahakundli": "mahakundli",
  "/lalkitab": "lalkitab",
  "/bnn": "bnn",
  "/raj-yog": "rajyog",
  "/life-report": "lifereport",
  "/palmistry-report": "palmistry",
  "/face-reading-report": "face",
  "/numerology-report": "numerology",
  "/astro-report": "astro",
};
function _reportTypeFromPath() {
  try { return REPORT_PATHS[window.location.pathname.replace(/\/+$/, "")] || null; }
  catch { return null; }
}

// Pre-built event helpers
export const analytics = {
  // Auth events
  signUp: (method = 'email') => trackEvent('sign_up', { method }),
  login: (method = 'email') => trackEvent('login', { method }),

  // Funnel: buyer opened the report form (birth-details modal)
  reportFormOpen: (type) => {
    const t = type || _reportTypeFromPath();
    if (t) trackEvent('report_form_open', { report_type: t });
  },

  // Report events
  reportDownload: (reportType, price = 0) => trackEvent('report_download', {
    report_type: reportType,
    value: price,
    currency: 'INR'
  }),
  reportGenerate: (reportType) => trackEvent('report_generate_start', {
    report_type: reportType
  }),

  // Purchase events (Razorpay)
  purchase: (transactionId, value, itemName) => {
    trackEvent('purchase', {
      transaction_id: transactionId,
      value: value,
      currency: 'INR',
      items: [{ item_name: itemName }],
      ...getUTM(),
    });
    fbq('track', 'Purchase', { value: value, currency: 'INR', content_name: itemName });
  },
  beginCheckout: (value, itemName) => {
    trackEvent('begin_checkout', {
      value: value,
      currency: 'INR',
      items: [{ item_name: itemName }],
      ...getUTM(),
    });
    fbq('track', 'InitiateCheckout', { value: value, currency: 'INR', content_name: itemName });
  },

  // Tool usage
  toolUse: (toolName) => trackEvent('tool_use', { tool_name: toolName }),
  
  // Project events
  projectCreate: () => trackEvent('project_create'),
  projectAnalyze: () => trackEvent('project_analyze'),
  
  // CRM events
  clientAdd: () => trackEvent('crm_client_add'),
  remedySchedule: () => trackEvent('crm_remedy_schedule'),

  // Page-level tracking (for SPA navigation)
  pageView: (pagePath, pageTitle) => {
    gtag('config', GA_ID, {
      page_path: pagePath,
      page_title: pageTitle
    });
    fbq('track', 'PageView');
    _send('pageview', pageTitle || pagePath);
  },
  captureUTM,
  getUTM,
};
